import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Plus, Search, Filter, Clock, CheckCircle2, 
  AlertCircle, Send, ShieldAlert, ArrowUpRight, Crown, Globe,
  Building2, Users, X, RefreshCw
} from 'lucide-react';
import { managerAPI } from '../utils/api';

const priorityColors = {
  Urgent: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/30' },
  High: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  Medium: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  Low: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30' }
};

const statusColors = {
  open: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  pending: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30' },
  resolved: { bg: 'bg-indigo-500/15', text: 'text-indigo-400', border: 'border-indigo-500/30' }
};

const ManagerSupport = () => {
  const [activeTab, setActiveTab] = useState('my'); // 'my' or 'team'
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Selected Ticket for Chat/Thread
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  // New Ticket Modal State
  const [showNewModal, setShowNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    subject: '',
    category: 'Target & Commission Issue',
    priority: 'High',
    message: '',
    contact: ''
  });

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await managerAPI.getTickets({ scope: activeTab });
      if (res.data?.success) {
        setTickets(res.data.data || []);
        if (selectedTicket) {
          const updated = (res.data.data || []).find(t => t.id === selectedTicket.id);
          setSelectedTicket(updated || null);
        }
      }
    } catch (err) {
      console.error("Failed to fetch tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [activeTab]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;
    try {
      setSubmitting(true);
      const res = await managerAPI.createTicket(form);
      if (res.data?.success) {
        setShowNewModal(false);
        setForm({
          subject: '',
          category: 'Target & Commission Issue',
          priority: 'High',
          message: '',
          contact: ''
        });
        setActiveTab('my');
        fetchTickets();
      }
    } catch (err) {
      console.error("Failed to create ticket:", err);
      alert(err.response?.data?.message || "Failed to submit complaint");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;
    try {
      setReplyLoading(true);
      const res = await managerAPI.replyTicket(selectedTicket.id, { message: replyText });
      if (res.data?.success) {
        setReplyText('');
        fetchTickets();
      }
    } catch (err) {
      console.error("Failed to send reply:", err);
    } finally {
      setReplyLoading(false);
    }
  };

  const filteredTickets = tickets.filter(t => {
    if (statusFilter !== 'ALL') {
      const s = (t.status || '').toLowerCase();
      if (statusFilter === 'open' && s !== 'open') return false;
      if (statusFilter === 'pending' && s !== 'pending') return false;
      if (statusFilter === 'resolved' && s !== 'resolved' && s !== 'solved') return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchSub = (t.subject || '').toLowerCase().includes(q);
      const matchMsg = (t.message || '').toLowerCase().includes(q);
      const matchId = (t.ticket_id || '').toLowerCase().includes(q);
      const matchUser = `${t.first_name || ''} ${t.last_name || ''}`.toLowerCase().includes(q);
      return matchSub || matchMsg || matchId || matchUser;
    }
    return true;
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Top Banner */}
      <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-r from-indigo-950/70 via-purple-950/60 to-slate-900 border border-indigo-500/20 p-8 shadow-2xl">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-bold uppercase tracking-wider mb-3">
              <MessageSquare size={14} className="text-indigo-400" />
              <span>Manager Support & Complaints Hub</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
              Executive Help & Grievances
            </h1>
            <p className="text-slate-400 text-sm mt-1 max-w-xl">
              File direct complaints to Admin Panel and supervise country-wide tickets submitted by BDs, agencies, and network hosts.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowNewModal(true)}
              className="px-5 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-sm shadow-xl shadow-indigo-600/30 flex items-center gap-2 transition-all hover:scale-105 cursor-pointer"
            >
              <Plus size={18} />
              <span>Submit Complaint to Admin</span>
            </button>
            <button
              onClick={fetchTickets}
              className="p-3 rounded-2xl bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 transition-colors cursor-pointer"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setActiveTab('my'); setSelectedTicket(null); }}
            className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === 'my'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-white/5 text-slate-400 hover:text-white'
            }`}
          >
            <Crown size={14} />
            <span>My Complaints to Admin</span>
          </button>
          <button
            onClick={() => { setActiveTab('team'); setSelectedTicket(null); }}
            className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === 'team'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-white/5 text-slate-400 hover:text-white'
            }`}
          >
            <Globe size={14} />
            <span>Assigned Country Complaints</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Search tickets, IDs, or users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-xs font-medium text-white placeholder-slate-500 outline-none focus:border-indigo-500 w-52 sm:w-64"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs font-bold text-slate-300 outline-none focus:border-indigo-500 cursor-pointer"
          >
            <option value="ALL" className="bg-slate-900">All Status</option>
            <option value="open" className="bg-slate-900">Open</option>
            <option value="pending" className="bg-slate-900">Pending</option>
            <option value="resolved" className="bg-slate-900">Resolved</option>
          </select>
        </div>
      </div>

      {/* 2-Column Layout: Tickets List + Thread Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Tickets List */}
        <div className={selectedTicket ? "lg:col-span-5 space-y-3" : "lg:col-span-12 space-y-3"}>
          {loading ? (
            <div className="p-16 text-center text-slate-500 bg-white/[0.02] rounded-3xl border border-white/5">
              Loading tickets...
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="p-16 text-center text-slate-500 bg-white/[0.02] rounded-3xl border border-white/5">
              <MessageSquare size={36} className="mx-auto text-slate-600 mb-3" />
              <p className="font-bold text-sm text-slate-400">No complaints or tickets found</p>
              <p className="text-xs text-slate-600 mt-1">
                {activeTab === 'my' 
                  ? "You haven't filed any complaints to the Admin Panel yet." 
                  : "No tickets filed by hosts or agencies in your assigned country."}
              </p>
            </div>
          ) : (
            filteredTickets.map((t) => {
              const isSelected = selectedTicket?.id === t.id;
              const pStyle = priorityColors[t.priority] || priorityColors.Medium;
              const sKey = (t.status || 'open').toLowerCase();
              const sStyle = statusColors[sKey] || statusColors.open;

              return (
                <div
                  key={t.id}
                  onClick={() => setSelectedTicket(t)}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-indigo-950/30 border-indigo-500/50 shadow-lg shadow-indigo-950/40'
                      : 'bg-white/[0.03] hover:bg-white/[0.06] border-white/5'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-black text-indigo-400">{t.ticket_id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${pStyle.bg} ${pStyle.text} ${pStyle.border}`}>
                        {t.priority}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/5 text-slate-300">
                        {t.user_type || 'User'}
                      </span>
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold capitalize border ${sStyle.bg} ${sStyle.text} ${sStyle.border}`}>
                      {t.status || 'Open'}
                    </span>
                  </div>

                  <h3 className="font-bold text-sm text-white line-clamp-1 mb-1">{t.subject || t.category}</h3>
                  <p className="text-xs text-slate-400 line-clamp-2 mb-3">{t.message}</p>

                  <div className="flex items-center justify-between text-[11px] text-slate-500 font-medium pt-2 border-t border-white/5">
                    <span>From: <strong className="text-slate-300">{t.first_name || 'User'} {t.last_name || ''}</strong></span>
                    <span>{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right Column: Selected Ticket Thread */}
        {selectedTicket && (
          <div className="lg:col-span-7 bg-[#0f1124] border border-indigo-500/20 rounded-[32px] overflow-hidden shadow-2xl flex flex-col min-h-[600px] sticky top-24">
            {/* Header */}
            <div className="p-6 border-b border-indigo-500/10 flex items-start justify-between bg-gradient-to-r from-indigo-950/40 to-slate-900/60">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono text-sm font-black text-indigo-400">{selectedTicket.ticket_id}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">
                    {selectedTicket.category}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/5 text-slate-400">
                    {selectedTicket.user_type}
                  </span>
                </div>
                <h2 className="text-lg font-black text-white">{selectedTicket.subject}</h2>
                <div className="text-xs text-slate-400 mt-0.5">
                  Submitted by: <strong className="text-white">{selectedTicket.first_name} {selectedTicket.last_name}</strong>
                  {selectedTicket.contact && <span> • Contact: {selectedTicket.contact}</span>}
                </div>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Conversation Thread */}
            <div className="flex-1 p-6 space-y-4 overflow-y-auto max-h-[460px]">
              {/* Original Message */}
              <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/5">
                <div className="flex items-center justify-between text-xs text-slate-400 font-bold mb-2">
                  <span className="text-indigo-400 font-bold uppercase tracking-wider">Initial Statement</span>
                  <span>{selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : ''}</span>
                </div>
                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                  {selectedTicket.message}
                </p>
              </div>

              {/* Replies */}
              {(selectedTicket.replies || []).map((reply, idx) => {
                const isSystem = reply.sender === 'System';
                const isManager = reply.sender === 'Manager';
                const isAdmin = reply.sender === 'Admin' || reply.sender === 'Super Admin';

                return (
                  <div
                    key={idx}
                    className={`p-4 rounded-2xl border ${
                      isAdmin
                        ? 'bg-gradient-to-r from-amber-950/30 to-purple-950/20 border-amber-500/30'
                        : isManager
                        ? 'bg-indigo-950/30 border-indigo-500/30'
                        : 'bg-white/[0.03] border-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className={`font-bold flex items-center gap-1.5 ${
                        isAdmin ? 'text-amber-400' : isManager ? 'text-indigo-400' : 'text-slate-400'
                      }`}>
                        {isAdmin && <Crown size={13} />}
                        {reply.sender_name || reply.sender}
                      </span>
                      <span className="text-[11px] text-slate-500 font-mono">
                        {reply.time || reply.timestamp ? new Date(reply.time || reply.timestamp).toLocaleTimeString() : ''}
                      </span>
                    </div>
                    <p className="text-sm text-slate-200 whitespace-pre-wrap">{reply.message}</p>
                  </div>
                );
              })}
            </div>

            {/* Reply Input Bar */}
            <form onSubmit={handleSendReply} className="p-4 border-t border-indigo-500/10 bg-black/40 flex items-center gap-3">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your message or update on this complaint..."
                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-500"
              />
              <button
                type="submit"
                disabled={replyLoading || !replyText.trim()}
                className="px-5 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-sm shadow-lg shadow-indigo-600/30 transition-all disabled:opacity-50 flex items-center gap-2 cursor-pointer"
              >
                <Send size={16} />
                <span className="hidden sm:inline">Send</span>
              </button>
            </form>
          </div>
        )}
      </div>

      {/* New Complaint / Ticket to Admin Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f1026] border border-indigo-500/20 rounded-[32px] p-8 max-w-lg w-full shadow-2xl relative">
            <button
              onClick={() => setShowNewModal(false)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/30">
                <Crown size={22} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">File Complaint to Admin Panel</h3>
                <p className="text-xs text-slate-400">Direct escalation from Executive Manager to Super Admin</p>
              </div>
            </div>

            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1.5 uppercase tracking-wider">Subject / Issue Title</label>
                <input
                  type="text"
                  required
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="e.g. Target threshold mismatch for Host Team in India"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 outline-none focus:border-indigo-500 text-sm font-semibold"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1.5 uppercase tracking-wider">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-3 text-white text-xs font-bold outline-none focus:border-indigo-500 cursor-pointer"
                  >
                    <option value="Target & Commission Issue">Target & Commission</option>
                    <option value="Host / Agency Dispute">Host / Agency Dispute</option>
                    <option value="BD Leadership Escalation">BD Leadership Escalation</option>
                    <option value="Payment / Wallet Verification">Payment / Wallet Issue</option>
                    <option value="Technical / System Glitch">Technical Glitch</option>
                    <option value="Other Executive Request">Other Executive Request</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1.5 uppercase tracking-wider">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-3 text-white text-xs font-bold outline-none focus:border-indigo-500 cursor-pointer"
                  >
                    <option value="Urgent">Urgent</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1.5 uppercase tracking-wider">Detailed Complaint Description</label>
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Explain the grievance, relevant Host or BD IDs, dates, and requested resolution..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 outline-none focus:border-indigo-500 text-sm font-medium"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1.5 uppercase tracking-wider">Contact / Callback Phone (Optional)</label>
                <input
                  type="text"
                  value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  placeholder="Direct phone number or email"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 outline-none focus:border-indigo-500 text-sm"
                />
              </div>

              <div className="pt-3 flex justify-end gap-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="px-5 py-2.5 rounded-xl text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 text-xs font-bold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? 'Submitting...' : 'Submit to Super Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerSupport;
